/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import find from 'lodash.find';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';

import * as CardActions from '../actions/cards';
import * as McMetaActions from '../actions/mc-meta';
import * as JourneyBuilderActions from '../actions/journey-templates';
import CalendarContainer from './calendar'; // eslint-disable-line import/no-named-as-default
import JourneyTemplates from './journey-templates';
// import WelcomeMat from './welcome-mat';
import CardGrid from '../components/card-grid';
import mcLib from '../api/mc-lib';

// Cards
import EinsteinLearningCenterCard from '../components/cards/einstein-learning-center';
import RecentlyEditedEmailsCard from '../components/cards/recently-edited-emails';
import RecentlySentEmailsCard from '../components/cards/recently-sent-emails';
import RecentlyRunAutomationsCard from '../components/cards/recently-run-automations';
import RecentlyEditedJourneysCard from '../components/cards/recently-edited-journeys';
import SalesforceBlogCard from '../components/cards/salesforce-blog';
import SuccessCommunityCard from '../components/cards/success-community';

import '../styles/app.scss';

let errorLimit = 10;

function handleDeepLink(options) {
  const name = options.analyticsName ? options.analyticsName : options.appName;
  mcLib.logEvent('deep link', name + (options.link ? '' : ' View All'));
  mcLib.appDeeplink(options);
}

// Hide card if user does not have the correct permissions to access the card
function componentShouldHide(toggleProp) {
  // This is intended to be used for local testing to ensure all cards are displayed
  if (mcLib.origin.match('localhost:8080')) {
    return false;
  }
  return !toggleProp;
}

function cardOrderDefault(cardList) {
  return [
    cardList.SuccessCommunityCard,
    cardList.SalesforceBlogCard,
    cardList.EinsteinLearningCenterCard,
    cardList.RecentlySentEmailsCard,
    cardList.RecentlyEditedEmailsCard,
    cardList.RecentlyRunAutomationsCard,
    cardList.RecentlyEditedJourneysCard,
  ];
}
function handleMcMetaActions(
  {
    hasJourneyBuilderAccess,
    hasAutomationStudioAccess,
    hasEmailStudioAccess,
    hasEmailStudioTrackingAccess,
    hasContentBuilderAccess,
  },
  props
) {
  if (!hasJourneyBuilderAccess) props.mcMetaActions.hasJourneyBuilderAccessPermission();
  if (!hasAutomationStudioAccess) props.mcMetaActions.hasAutomationStudioAccessPermission();
  if (!hasEmailStudioAccess) props.mcMetaActions.hasEmailStudioAccessPermission();
  if (!hasEmailStudioTrackingAccess) props.mcMetaActions.hasEmailStudioTrackingAccessPermission();
  if (!hasContentBuilderAccess) props.mcMetaActions.hasContentBuilderAccessPermission();
}
export function App(props) {
  const previousProps = useRef(props);
  const appDataTimeout = useRef();

  const getAppData = useCallback(
    ({
      isAutomationStudioProvisioned,
      isEmailStudioProvisioned,
      isJourneyBuilderProvisioned,
      hasJourneyBuilderBusinessRule,
      hasLightningHomepageBusinessRule,
      hasJourneyBuilderAccess,
      hasAutomationStudioAccess,
      hasEmailStudioAccess,
      hasEmailStudioTrackingAccess,
      hasContentBuilderAccess,
    }) => {
      if (appDataTimeout.current) {
        clearTimeout(appDataTimeout.current);
        appDataTimeout.current = null;
      }

      props.mcMetaActions.getProvisionedApplications();
      props.mcMetaActions.getUserInfo();
      props.cardActions.getSalesforceBlogPosts();

      handleMcMetaActions(
        {
          hasJourneyBuilderAccess,
          hasAutomationStudioAccess,
          hasEmailStudioAccess,
          hasEmailStudioTrackingAccess,
          hasContentBuilderAccess,
        },
        props
      );

      if (isAutomationStudioProvisioned && hasAutomationStudioAccess) {
        props.cardActions.getRecentAutomationsRun();
      }

      if (isEmailStudioProvisioned && hasEmailStudioAccess) {
        props.cardActions.getRecentEmailsEdited();
      }

      if (isEmailStudioProvisioned && hasEmailStudioTrackingAccess) {
        props.cardActions.getRecentEmailsSent();
      }

      if (
        isJourneyBuilderProvisioned &&
        hasJourneyBuilderBusinessRule &&
        hasLightningHomepageBusinessRule &&
        hasJourneyBuilderAccess
      ) {
        props.cardActions.getRecentJourneysEdited();
      }

      if (hasLightningHomepageBusinessRule === undefined) props.journeyBuilderActions.hasLightningHomepageBR();
      if (hasJourneyBuilderBusinessRule === undefined) props.journeyBuilderActions.hasJourneyBuilderBR();

      if (!appDataTimeout.current) {
        // Update every 5 minutes
        appDataTimeout.current = setTimeout(() => getAppData(props), 300000);
      }
    },
    [props]
  );

  useEffect(() => {
    const prevProps = previousProps.current;

    if (
      (props.isAutomationStudioProvisioned && !prevProps.isAutomationStudioProvisioned) ||
      (props.isEmailStudioProvisioned && !prevProps.isEmailStudioProvisioned) ||
      (props.isJourneyBuilderProvisioned && !prevProps.isJourneyBuilderProvisioned) ||
      (props.hasJourneyBuilderBusinessRule && !prevProps.hasJourneyBuilderBusinessRule) ||
      (props.hasLightningHomepageBusinessRule && !prevProps.hasLightningHomepageBusinessRule) ||
      (props.hasJourneyBuilderAccess && !prevProps.hasJourneyBuilderAccess) ||
      (props.hasAutomationStudioAccess && !prevProps.hasAutomationStudioAccess) ||
      (props.hasEmailStudioAccess && !prevProps.hasEmailStudioAccess) ||
      (props.hasEmailStudioTrackingAccess && !prevProps.hasEmailStudioTrackingAccess) ||
      (props.hasContentBuilderAccess && !prevProps.hasContentBuilderAccess)
    ) {
      getAppData(props);
    } else if (
      (props.recentAutomationsRun && props.recentAutomationsRun.error && !prevProps.recentAutomationsRun.error) ||
      (props.recentEmailsEdited && props.recentEmailsEdited.error && !prevProps.recentEmailsEdited.error) ||
      (props.recentEmailsSent && props.recentEmailsSent.error && !prevProps.recentEmailsSent.error) ||
      (props.recentJourneysEdited && props.recentJourneysEdited.error && !prevProps.recentJourneysEdited.error) ||
      (props.salesforceBlogPosts && props.salesforceBlogPosts.error && !prevProps.salesforceBlogPosts.error)
    ) {
      if (errorLimit > 0) {
        getAppData(props);
      }
      errorLimit--;
    }

    previousProps.current = props;
  }, [props]);

  useEffect(() => {
    getAppData(props);
    return () => {
      if (appDataTimeout.current) {
        clearTimeout(appDataTimeout.current);
        appDataTimeout.current = null;
      }
    };
  }, []);

  const getCardList = useCallback(() => {
    const cardList = {
      RecentlySentEmailsCard: (
        <RecentlySentEmailsCard
          key="recentlySentEmailsCard"
          localizations={props.localizations}
          onDeepLink={handleDeepLink}
          sentEmails={props.recentEmailsSent}
          unprovisioned={!props.isEmailStudioProvisioned}
          hide={componentShouldHide(props.hasEmailStudioTrackingAccess)}
        />
      ),
      RecentlyEditedEmailsCard: (
        <RecentlyEditedEmailsCard
          key="recentlyEditedEmailsCard"
          editedEmails={props.recentEmailsEdited}
          localizations={props.localizations}
          onDeepLink={handleDeepLink}
          unprovisioned={!props.isEmailStudioProvisioned}
          hide={componentShouldHide(props.hasEmailStudioAccess && props.hasContentBuilderAccess)}
        />
      ),
      EinsteinLearningCenterCard: (
        <EinsteinLearningCenterCard
          key="einsteinLearningCenterCard"
          className="einstein-learning-center"
          heading={props.localizations['homepage.cards.learning_center.heading']}
          localizations={props.localizations}
          onDeepLink={handleDeepLink}
        />
      ),
      SalesforceBlogCard: (
        <SalesforceBlogCard
          key="salesforceBlogCard"
          heading={props.localizations['homepage.cards.blog.heading']}
          posts={props.salesforceBlogPosts}
          localizations={props.localizations}
        />
      ),
      RecentlyRunAutomationsCard: (
        <RecentlyRunAutomationsCard
          key="recentlyRunAutomationsCard"
          runAutomations={props.recentAutomationsRun}
          localizations={props.localizations}
          onDeepLink={handleDeepLink}
          unprovisioned={!props.isAutomationStudioProvisioned}
          hide={componentShouldHide(props.hasAutomationStudioAccess)}
        />
      ),
      RecentlyEditedJourneysCard: (
        <RecentlyEditedJourneysCard
          key="recentlyEditedJourneysCard"
          editedJourneys={props.recentJourneysEdited}
          localizations={props.localizations}
          onDeepLink={handleDeepLink}
          unprovisioned={!props.isJourneyBuilderProvisioned}
          hide={componentShouldHide(props.hasJourneyBuilderAccess)}
        />
      ),
      SuccessCommunityCard: (
        <SuccessCommunityCard
          key="successCommunityCard"
          heading={props.localizations['homepage.cards.success_community.heading']}
          localizations={props.localizations}
        />
      ),
    };

    return cardOrderDefault(cardList);
  }, [props]);

  return (
    <div className="mc-homepage slds-p-horizontal--large">
      {/* <WelcomeMat /> */}
      <JourneyTemplates />
      <CalendarContainer />
      <CardGrid>{getCardList()}</CardGrid>
    </div>
  );
}

App.displayName = 'App';

App.propTypes = {
  cardActions: PropTypes.object,
  isAutomationStudioProvisioned: PropTypes.bool,
  isEmailStudioProvisioned: PropTypes.bool,
  isJourneyBuilderProvisioned: PropTypes.bool,
  hasJourneyBuilderBusinessRule: PropTypes.bool,
  hasLightningHomepageBusinessRule: PropTypes.bool,
  hasJourneyBuilderAccess: PropTypes.bool,
  hasAutomationStudioAccess: PropTypes.bool,
  hasEmailStudioAccess: PropTypes.bool,
  hasEmailStudioTrackingAccess: PropTypes.bool,
  hasContentBuilderAccess: PropTypes.bool,
  localizations: PropTypes.object,
  mcMetaActions: PropTypes.object,
  journeyBuilderActions: PropTypes.object,
  recentAutomationsRun: PropTypes.array,
  recentEmailsEdited: PropTypes.array,
  recentEmailsSent: PropTypes.array,
  recentJourneysEdited: PropTypes.array,
  salesforceBlogPosts: PropTypes.array,
};

function isAppProvisioned(apps = [], appName) {
  return !!find(apps, {
    name: appName,
  });
}

function mapStateToProps(state, ownProps) {
  const isAutomationStudioProvisioned = isAppProvisioned(state.applications, 'Automation Studio');
  const isEmailStudioProvisioned = isAppProvisioned(state.applications, 'Email');
  const isJourneyBuilderProvisioned = isAppProvisioned(state.applications, 'Journey Builder');

  return {
    filter: ownProps.location.query.filter,
    isAutomationStudioProvisioned,
    isEmailStudioProvisioned,
    isJourneyBuilderProvisioned,
    hasJourneyBuilderBusinessRule: state.businessRules.hasJourneyBuilderBusinessRule,
    hasLightningHomepageBusinessRule: state.businessRules.hasLightningHomepageBusinessRule,
    localizations: state.localization.localizations,
    pathname: ownProps.location.pathname,
    recentAutomationsRun: state.recentAutomationsRun,
    recentEmailsEdited: state.recentEmailsEdited,
    recentEmailsSent: state.recentEmailsSent,
    recentJourneysEdited: state.recentJourneysEdited,
    salesforceBlogPosts: state.salesforceBlogPosts,
    hasJourneyBuilderAccess: state.permissions.hasJourneyBuilderAccess,
    hasAutomationStudioAccess: state.permissions.hasAutomationStudioAccess,
    hasEmailStudioAccess: state.permissions.hasEmailStudioAccess,
    hasEmailStudioTrackingAccess: state.permissions.hasEmailStudioTrackingAccess,
    hasContentBuilderAccess: state.permissions.hasContentBuilderAccess,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    cardActions: bindActionCreators(CardActions, dispatch),
    mcMetaActions: bindActionCreators(McMetaActions, dispatch),
    journeyBuilderActions: bindActionCreators(JourneyBuilderActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
